import React from "react";
import { HalfTimeDraw } from "src/app/interfaces/Index";
import { FixtureService } from "src/app/services/Index";
import "./ScoreboardHalftimeDraw.css";

type Props = {};
type State = {
	halfTimeDraw: HalfTimeDraw;
	/** How many places have been revealed so far. */
	revealed: number;
};

/** How long between each place appearing. */
const REVEAL_GAP_MS = 1400;

/**
 * A drawn place. Nothing at all if the club hasn't entered one — showing
 * "3rd - -" on a board the whole ground can see is worse than showing two.
 */
function Place({ label, number, prize, shown }: {
	label: string; number?: number | null; prize?: number | null; shown: boolean;
}) {
	if (number === null || number === undefined || Number(number) === 0) return null;

	return (
		<h2 className={`htd-place${shown ? " in" : ""}`}>
			{label} - {number}
			{/* Only when there is one. This used to print a bare "£" with nothing
			    after it, which is what a full ground was looking at. */}
			{prize !== null && prize !== undefined && Number(prize) > 0 && <> - £{prize}</>}
		</h2>
	);
}

/**
 * The half-time draw board.
 *
 * Places come up one at a time, third first. A draw is a moment — announcing
 * the winner last is how every raffle in the country is run, and a board that
 * shows all three at once throws that away. The delay is long enough to read a
 * five-digit number across a stand and short enough to finish inside half time.
 *
 * Honours prefers-reduced-motion: everything appears at once instead. Nobody
 * should have to sit through movement they've asked their device not to make.
 */
export class ScoreboardHalftimeDraw extends React.Component<Props, State> {
	private timers: any[] = [];

	constructor(props: Props) {
		super(props);
		this.state = { halfTimeDraw: new HalfTimeDraw, revealed: 0 };
	}

	componentDidMount() {
		this.getHalfTimeDraw();
	}

	componentWillUnmount() {
		this.timers.forEach(clearTimeout);
	}

	/** Bring the places up in order, once the numbers are in. */
	startReveal(draw: HalfTimeDraw) {
		this.timers.forEach(clearTimeout);
		this.timers = [];

		const places = [draw.htDrawThird, draw.htDrawSecond, draw.htDrawFirst]
			.filter((n) => n !== null && n !== undefined && Number(n) !== 0).length;

		if (!places) return;

		const still = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
		if (still) {
			this.setState({ revealed: places });
			return;
		}

		for (let i = 1; i <= places; i++) {
			this.timers.push(setTimeout(() => this.setState({ revealed: i }), i * REVEAL_GAP_MS));
		}
	}

	render() {
		const draw = this.state.halfTimeDraw;
		const { revealed } = this.state;

		// Revealed third, second, first — so "revealed" counts up from the
		// bottom of the board while reading top to bottom stays correct.
		const shown = (place: 1 | 2 | 3) => revealed >= 4 - place;

		return (
			<div className="htd-board" id="scoreboard-wrapper" style={{textAlign: "center", width:576, height:432}}>
				<h1>HALF TIME DRAW NUMBERS</h1>
				<br/>
				<Place label="1st" number={draw.htDrawFirst} prize={draw.htDrawFirstPrize} shown={shown(1)} />
				<Place label="2nd" number={draw.htDrawSecond} prize={draw.htDrawSecondPrize} shown={shown(2)} />
				<Place label="3rd" number={draw.htDrawThird} prize={draw.htDrawThirdPrize} shown={shown(3)} />

				{/* Last, and only where a sponsor actually backs the draw. */}
				{(draw.sponsorImage || draw.sponsorName) &&
					<div className={`htd-sponsor${revealed > 0 ? " in" : ""}`}>
						<span>SPONSORED BY</span>
						{draw.sponsorImage
							? <img alt={draw.sponsorName || ""} src={draw.sponsorImage} />
							: <strong>{draw.sponsorName}</strong>}
					</div>}
			</div>
		);
	}

	async getHalfTimeDraw(){
		try {
			const res = await FixtureService.getHalfTimeDraw();
			if (!res.ok) return;

			const body = await res.json();
			if (body && !body.error) {
				this.setState({ halfTimeDraw: body, revealed: 0 }, () => this.startReveal(body));
			}
		} catch {
			// Leave whatever is on the board. A scoreboard that blanks itself
			// because one request failed is worse than one showing the last
			// numbers it had.
		}
	}
}
